import React, { useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AppShell from '../../components/layout/AppShell';
import { ArrowLeft, Clock, ChevronRight, Users, Zap, Search, X } from 'lucide-react';
import { useChallenges } from '../../features/cases/hooks/use-challenges';

const ChallengePage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const q = searchParams.get('q') || '';
  const { data, isLoading, isError } = useChallenges();

  const challenges = data?.data || [];

  const clearSearch = () => {
    const newParams = new URLSearchParams(searchParams);
    newParams.delete('q');
    setSearchParams(newParams);
  };

  const filteredChallenges = useMemo(() => {
    if (!q.trim()) return challenges;
    const lower = q.toLowerCase();
    return challenges.filter((ch) =>
      ch.title?.toLowerCase().includes(lower) ||
      ch.species?.toLowerCase().includes(lower) ||
      (ch.clinicalSigns || []).some((s) => s.toLowerCase().includes(lower))
    );
  }, [challenges, q]);

  const getSymptomPills = (ch) => {
    const signs = ch.clinicalSigns || [];
    const visible = signs.slice(0, 3);
    const extra = signs.length - 3;
    return { visible, extra };
  };

  return (
    <AppShell>
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Back Button */}
        <button
          onClick={() => navigate('/dashboard')}
          className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800 mb-6 transition-colors group"
        >
          <ArrowLeft size={16} className="group-hover:-translate-x-0.5 transition-transform" />
          <span>Back</span>
        </button>

        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-2">
            <div className="p-2 bg-[#ede9fe] rounded-lg">
              <Zap size={20} className="text-[#6b46c1]" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900">Case Challenges</h1>
          </div>
          <p className="text-gray-500 text-sm">
            Think like a veterinarian. Work through real clinical scenarios and submit your diagnostic approach.
          </p>
        </div>

        {isLoading && (
          <div className="flex justify-center py-20">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#6b46c1]" />
          </div>
        )}

        {isError && (
          <div className="text-center py-20 text-gray-500">
            <p>Failed to load challenges. Please try again.</p>
          </div>
        )}

        {!isLoading && challenges.length === 0 && (
          <div className="text-center py-20 text-gray-500">
            <Zap size={40} className="mx-auto mb-3 text-gray-300" />
            <p className="font-medium">No challenges available yet.</p>
            <p className="text-sm mt-1">Check back soon — veterinarians are adding new cases!</p>
          </div>
        )}

        {/* Search context banner */}
        {q && (
          <div className="mb-4 p-3 bg-indigo-50 border border-indigo-100 rounded-lg flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm text-indigo-900">
              <Search size={16} className="text-indigo-600" />
              <span>Showing challenges matching "<span className="font-semibold">{q}</span>"</span>
            </div>
            <button
              onClick={clearSearch}
              className="flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-800 font-medium transition-colors"
            >
              <X size={14} /> Clear search
            </button>
          </div>
        )}

        {/* No results from search */}
        {!isLoading && q && filteredChallenges.length === 0 && challenges.length > 0 && (
          <div className="text-center py-20 text-gray-500">
            <Search size={40} className="mx-auto mb-3 text-gray-300" />
            <p className="font-medium">No challenges match "{q}"</p>
            <p className="text-sm mt-1">
              Try a different keyword or{' '}
              <button onClick={clearSearch} className="text-indigo-600 hover:underline font-medium">clear search</button>
            </p>
          </div>
        )}

        <div className="space-y-4">
          {filteredChallenges.map((ch) => {
            const { visible: pillSigns, extra } = getSymptomPills(ch);
            return (
              <div
                key={ch.id}
                onClick={() => navigate(`/challenges/${ch.id}/intake`)}
                className="bg-white border border-gray-200 rounded-xl p-5 cursor-pointer hover:border-[#6b46c1] hover:shadow-md transition-all duration-200 group"
              >
                {/* Top row: species, time, arrow */}
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <span className="px-2.5 py-0.5 bg-[#ede9fe] text-[#6b46c1] text-xs font-semibold rounded-full">
                      {ch.species}
                    </span>
                    <span className="flex items-center gap-1 text-xs text-gray-400 border border-gray-200 rounded-full px-2.5 py-0.5">
                      <Clock size={11} /> ~{ch.estimatedMins} min
                    </span>
                  </div>
                  <ChevronRight size={18} className="text-gray-300 group-hover:text-[#6b46c1] transition-colors" />
                </div>

                {/* Title */}
                <h2 className="text-base font-semibold text-[#6b46c1] mb-2 group-hover:text-[#5a38a8]">
                  {ch.title}
                </h2>

                {/* Symptom pills */}
                {pillSigns.length > 0 && (
                  <div className="mb-4">
                    <p className="text-xs text-gray-500 mb-2">Key symptoms to consider:</p>
                    <div className="flex flex-wrap gap-2">
                      {pillSigns.map((sign, i) => (
                        <span
                          key={i}
                          title={sign}
                          className="px-2.5 py-1 bg-gray-100 text-gray-600 text-xs rounded-md border border-gray-200 truncate max-w-full sm:max-w-[300px]"
                        >
                          {sign}
                        </span>
                      ))}
                      {extra > 0 && (
                        <span className="px-2.5 py-1 bg-gray-100 text-gray-500 text-xs rounded-md border border-gray-200">
                          +{extra} more
                        </span>
                      )}
                    </div>
                  </div>
                )}

                {/* Footer: attempts + start link */}
                <div className="flex items-center justify-between border-t border-gray-100 pt-3 mt-1">
                  <div className="flex items-center gap-1.5 text-xs text-gray-400">
                    <Users size={13} />
                    <span>{ch.attemptCount || 0} {ch.attemptCount === 1 ? 'attempt' : 'attempts'}</span>
                  </div>
                  <span className="text-xs font-semibold text-[#6b46c1] group-hover:underline">
                    Start Challenge →
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </AppShell>
  );
};

export default ChallengePage;
